import classNames from 'classnames';

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, `className`> {
    /**
     * The input name attribute
     */
    name?: string;

    /**
     * The input id attribute (defaults to name if not provided)
     */
    id?: string;

    /**
     * The input type (text, email, password, etc.)
     * @default "text"
     */
    type?: string;

    /**
     * The current value of the input
     */
    value?: string;

    /**
     * Change handler for the input (optional for disabled/read-only inputs)
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Placeholder text
     */
    placeholder?: string;

    /**
     * Optional label to display above the input
     */
    label?: string;

    /**
     * Error message to display below the input (string for single error, array for multiple errors shown as list)
     */
    error?: string | string[];

    /**
     * Helper text to display below the input (only shown when no error)
     */
    helperText?: string;

    /**
     * Whether the input is disabled
     * @default false
     */
    disabled?: boolean;

    /**
     * Whether the input is read-only
     * @default false
     */
    readOnly?: boolean;

    /**
     * Additional CSS classes to apply to the input
     */
    className?: string;

    /**
     * Additional CSS classes to apply to the container
     */
    containerClassName?: string;

    /**
     * Maximum length of the input
     */
    maxLength?: number;
}

/**
 * TextField component - A reusable text input with consistent styling across the app.
 *
 * Features:
 * - Consistent styling for normal, error, disabled, and read-only states
 * - Optional label above the input
 * - Helper text or error message below
 * - Hover and focus-visible states
 * - Full accessibility support with ARIA attributes
 *
 * @example
 * ```tsx
 * // Basic usage
 * <TextField
 *   name="email"
 *   type="email"
 *   placeholder="Email*"
 *   value={email}
 *   onChange={handleChange}
 *   error={errors.email}
 * />
 *
 * // With helper text
 * <TextField
 *   name="password"
 *   type="password"
 *   placeholder="Password*"
 *   value={password}
 *   onChange={handleChange}
 *   helperText="Use at least 9 characters"
 *   error={errors.password}
 * />
 *
 * // With label
 * <TextField
 *   name="fullName"
 *   label="Full Name*"
 *   value={fullName}
 *   onChange={handleChange}
 *   error={errors.fullName}
 * />
 *
 * // Read-only
 * <TextField
 *   name="email"
 *   value={email}
 *   readOnly
 * />
 * ```
 */
export const TextField: React.FC<TextFieldProps> = ({
    name,
    id,
    type = `text`,
    value,
    onChange,
    placeholder,
    label,
    error,
    helperText,
    disabled = false,
    readOnly = false,
    className,
    containerClassName,
    maxLength,
    ...rest
}) => {
    const inputId = id || name;
    const hasError = !!error;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    // Determine which describedby IDs to use
    const describedBy = [
        hasError && errorId,
        helperText && !hasError && helperId
    ].filter(Boolean).join(` `) || undefined;

    // If label is provided, use the labeled input pattern (Pattern B from upsert-payor)
    if (label) {
        return (
            <div className={containerClassName}>
                <div className={classNames(
                    `border rounded-lg px-4 py-2 flex flex-col transition-colors focus-within:outline-focus-ring`,
                    {
                        'bg-neutral-50 border-neutral-300 cursor-not-allowed': disabled,
                        'bg-white border-neutral-200 hover:border-neutral-300 focus-within:border-neutral-300': !disabled && !readOnly,
                        'bg-white read-only:bg-neutral-50 border-neutral-200': readOnly
                    }
                )}>
                    <label htmlFor={inputId} className="text-xs leading-4 text-neutral-700 tracking-[0.12px]">
                        {label}
                    </label>
                    <input
                        type={type}
                        id={inputId}
                        name={name}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        readOnly={readOnly}
                        maxLength={maxLength}
                        className={classNames(
                            `bg-transparent border-0 p-0 text-base leading-6 tracking-[0.16px] outline-none`,
                            {
                                'text-neutral-500 cursor-not-allowed': disabled,
                                'text-neutral-950': !disabled && !readOnly,
                                'text-neutral-700': readOnly
                            },
                            className
                        )}
                        aria-invalid={hasError}
                        aria-describedby={describedBy}
                        {...rest}
                    />
                </div>
                {hasError && (
                    Array.isArray(error) ? (
                        <ul className="mt-2 list-disc ml-4" id={errorId}>
                            {error.map((e, index) => (
                                <li key={index} className="text-xs leading-4 text-red-600">{e}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-2 text-xs leading-4 text-red-600" id={errorId}>
                            {error}
                        </p>
                    )
                )}
                {helperText && !hasError && (
                    <p className="mt-2 text-xs leading-4 text-neutral-700" id={helperId}>
                        {helperText}
                    </p>
                )}
            </div>
        );
    }

    // Standard input pattern (Pattern A from most other forms)
    return (
        <div className={containerClassName}>
            <input
                type={type}
                id={inputId}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
                readOnly={readOnly}
                maxLength={maxLength}
                className={classNames(
                    `block w-full rounded-lg border p-4 text-base transition-colors focus-visible:outline-focus-ring`,
                    {
                        // Disabled state
                        'bg-neutral-50 border-neutral-300 text-neutral-500 cursor-not-allowed': disabled,
                        // Read-only state
                        'read-only:bg-neutral-50 border-neutral-200 text-neutral-700': readOnly && !disabled,
                        // Normal/error state
                        'border-neutral-200 hover:border-neutral-300 text-neutral-950 placeholder:text-neutral-700': !disabled && !readOnly
                    },
                    className
                )}
                aria-invalid={hasError}
                aria-describedby={describedBy}
                {...rest}
            />
            {helperText && !hasError && (
                <p className="mt-2 px-4 text-xs leading-4 text-neutral-600" id={helperId}>
                    {helperText}
                </p>
            )}
            {hasError && (
                Array.isArray(error) ? (
                    <ul className="mt-2 px-4 list-disc ml-4" id={errorId}>
                        {error.map((e, index) => (
                            <li key={index} className="text-xs leading-4 text-red-600">{e}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="mt-2 px-4 text-xs leading-4 text-red-600" id={errorId}>
                        {error}
                    </p>
                )
            )}
        </div>
    );
};
